"use client";
import { useCallback, useEffect, useRef, useState } from "react";
import ReactMapGL, { AttributionControl, type MapRef, Marker, type ViewState } from "react-map-gl";
import "./mapbox-overrides.css";
import { getMapboxTokenFromEnv } from "../../config/mapbox";
import { useColorMode } from "../../utils/ColorModeProvider";
import { Dialog } from "../Dialog";
import { Icon } from "../Icon";
import { ControlButton } from "./controls/primitives/ControlButton";
import { InteractiveMap } from "./InteractiveMap";
import { type LayerSpec, renderLayers } from "./layers";
import { ensureMapboxCss } from "./loadMapboxCss";
import { MapLoadingOverlay } from "./MapLoadingOverlay";
import { MapPlaceholder } from "./MapPlaceholder";
import { getMapStyle, type MapType } from "./mapConfig";

export interface StaticMapProps {
  /**
   * Map width
   * @default "100%"
   */
  width?: number | string;
  /**
   * Map height
   * @default "100%"
   */
  height?: number | string;
  /**
   * Initial viewport configuration
   */
  initialViewState?: Partial<ViewState>;
  /**
   * Show loading skeleton
   */
  isLoading?: boolean;
  /**
   * Map type - the visual style of the map
   * Automatically adapts to light/dark mode via ColorModeProvider
   *
   * - "streets": Street map with roads and labels
   * - "satellite": Satellite imagery with street overlays
   * - "neutral": Minimal/clean map design
   *
   * @default "streets"
   */
  mapType?: MapType;
  /**
   * Layers to render on the map
   *
   * Use the layers API for clean, type-safe layer configuration.
   * Same API as InteractiveMap for consistency.
   *
   * @example
   * ```tsx
   * import { layer } from '@texturehq/edges';
   *
   * <StaticMap
   *   layers={[
   *     layer.geojson({
   *       id: 'points',
   *       data: myGeoJson,
   *       renderAs: 'circle',
   *       style: { color: { token: 'brand-primary' } }
   *     })
   *   ]}
   * />
   * ```
   */
  layers?: LayerSpec[];
  /**
   * Mapbox access token
   * @default getMapboxTokenFromEnv()
   */
  mapboxAccessToken?: string;
  /**
   * Show marker at center
   * @default true
   */
  showMarker?: boolean;
  /**
   * Show Mapbox attribution control
   * @default false
   */
  showAttribution?: boolean;
  /**
   * Callback fired when the map finishes loading
   * Useful for knowing when Mapbox is fully initialized and tiles are loaded
   */
  onLoad?: () => void;
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Show expand button to open interactive fullscreen map
   * When enabled, shows a button in the top-right corner that opens
   * the map in an interactive dialog
   * @default false
   */
  showExpandToggle?: boolean;
  /**
   * Title for the expanded map dialog
   * @default "Explore map"
   */
  expandedMapTitle?: string;
  /**
   * Optional address or location label to show in expanded view
   */
  expandedMapAddressLabel?: string;
}

/**
 * StaticMap
 *
 * A non-interactive map component for displaying a location.
 * Perfect for showing addresses, store locations, or any single point of interest.
 *
 * Automatically adapts to light/dark mode using the global ColorModeProvider.
 */
export function StaticMap({
  width = "100%",
  height = "100%",
  initialViewState = {
    longitude: -122.4194,
    latitude: 37.7749,
    zoom: 14,
  },
  isLoading = false,
  mapType = "streets",
  layers = [],
  mapboxAccessToken,
  showMarker = true,
  showAttribution = false,
  onLoad,
  className,
  showExpandToggle = false,
  expandedMapTitle = "Explore map",
  expandedMapAddressLabel,
}: StaticMapProps) {
  // Lazy evaluation of token - only call getMapboxTokenFromEnv() if not provided
  const token = mapboxAccessToken ?? getMapboxTokenFromEnv();
  const { isDarkTheme } = useColorMode();
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [showLoadingState, setShowLoadingState] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const mapRef = useRef<MapRef>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Handlers for expand dialog - memoized to avoid inline function warnings
  const handleExpandOpen = useCallback(() => setIsExpanded(true), []);
  const handleExpandClose = useCallback(() => setIsExpanded(false), []);

  useEffect(() => {
    void ensureMapboxCss();
  }, []);

  const containerStyle = {
    width: typeof width === "number" ? `${width}px` : width,
    height: typeof height === "number" ? `${height}px` : height,
  };

  // Delay showing loading state to avoid flash for fast loads
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!isMapLoaded) {
        setShowLoadingState(true);
      }
    }, 200); // 200ms delay before showing loading state

    return () => clearTimeout(timer);
  }, [isMapLoaded]);

  // Handle map load event
  const handleMapLoad = useCallback(() => {
    setIsMapLoaded(true);
    setShowLoadingState(false);
    onLoad?.();
  }, [onLoad]);

  // Handle container resize to trigger map resize
  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver(() => {
      // Trigger resize on the map instance
      if (mapRef.current) {
        mapRef.current.resize();
      }
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // Determine if we should show loading overlay
  const showLoadingOverlay = isLoading || showLoadingState;

  if (!token) {
    return <MapPlaceholder style={containerStyle} className={className} />;
  }

  return (
    <div ref={containerRef} style={containerStyle} className={`relative ${className || ""}`}>
      <ReactMapGL
        ref={mapRef}
        mapboxAccessToken={token}
        initialViewState={initialViewState}
        style={{ width: "100%", height: "100%" }}
        mapStyle={getMapStyle(mapType, isDarkTheme)}
        onLoad={handleMapLoad}
        scrollZoom={false}
        dragPan={false}
        dragRotate={false}
        doubleClickZoom={false}
        touchZoomRotate={false}
        interactive={false}
        attributionControl={false}
      >
        {/* Attribution in top-left corner */}
        {showAttribution && <AttributionControl position="top-left" />}

        {/* Render custom layers from layers API */}
        {renderLayers(layers, mapType)}

        {/* Show marker at center if enabled */}
        {showMarker && initialViewState.longitude !== undefined && initialViewState.latitude !== undefined && (
          <Marker longitude={initialViewState.longitude} latitude={initialViewState.latitude} anchor="bottom">
            <Icon name="MapPin" size={36} className="text-map-marker-default" weight="fill" />
          </Marker>
        )}
      </ReactMapGL>

      {/* Expand toggle button */}
      {showExpandToggle && (
        <div className="absolute top-4 right-4 z-20">
          <ControlButton icon="ArrowsOutSimple" ariaLabel="Expand map to fullscreen" onPress={handleExpandOpen} />
        </div>
      )}

      {/* Expanded map dialog - controlled mode for close icon support on mobile */}
      {showExpandToggle && (
        <Dialog
          isOpen={isExpanded}
          onClose={handleExpandClose}
          title={expandedMapTitle}
          contentPadding={false}
          maxWidth="min(1100px, 100vw)"
          minWidth="min(360px, 100vw)"
          height="80vh"
          mobileHeight="82vh"
        >
          <div className="flex flex-col h-full">
            <div className="flex-1 min-h-0 overflow-hidden sm:mx-4 sm:mt-4 sm:rounded-xl sm:border sm:border-border-default">
              <InteractiveMap
                initialViewState={initialViewState}
                mapType={mapType}
                mapboxAccessToken={token}
                showMarker={showMarker}
                markerLocation={
                  initialViewState.longitude !== undefined && initialViewState.latitude !== undefined
                    ? {
                        latitude: initialViewState.latitude,
                        longitude: initialViewState.longitude,
                      }
                    : undefined
                }
                layers={layers}
              />
            </div>

            {expandedMapAddressLabel && (
              <p className="text-sm text-text-muted px-4 py-3 flex-shrink-0">{expandedMapAddressLabel}</p>
            )}
          </div>
        </Dialog>
      )}

      {/* Loading overlay */}
      {showLoadingOverlay && <MapLoadingOverlay />}
    </div>
  );
}
